import CircularProgress, {
  CircularProgressProps,
  circularProgressClasses,
} from "@mui/material/CircularProgress";
import LottieInner from "react-lottie";
import BrandclubLoading from "../assets/lottie/brandclub_logo_load.json";
import DotLoading from "../assets/lottie/lottie_dot_loading.json";
import EllipsisLoading from "../assets/lottie/ellipsis.json";
import EllipsisBlueLoading from "../assets/lottie/ellipsis_darkblue.json";
import { useTheme } from "@mui/material";
import { progressLottieOptions } from "@/utils/lottieUtils";
import useStoreType from "@/utils/hooks/useStoreType";
import { StoreBrandingType } from "@/types/misc";
import { brandclub_colors } from "@brandclub/common-ui";

// @ts-ignore
const Lottie = LottieInner.default
  ? // @ts-ignore
    LottieInner.default
  : LottieInner;

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: BrandclubLoading,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const dotOptions = {
  loop: true,
  autoplay: true,
  animationData: DotLoading,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const ellipsisOptions = {
  loop: true,
  autoplay: true,
  animationData: EllipsisLoading,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

export const StoreSpin = ({
  size,
  thickness,
  spinColor,
  ...props
}: CircularProgressProps & {
  size?: number;
  thickness?: number;
  spinColor?: string;
}) => {
  const storeType = useStoreType();
  const isDtc = storeType === StoreBrandingType.CustomDTCStore;
  const {
    variant: _ignoredVariant,
    value: _ignoredValue,
    ...restProps
  } = props;

  return (
    <div
      style={{
        position: "relative",
        alignItems: "center",
        justifyContent: "center",
        display: "flex",
      }}
    >
      <CircularProgress
        {...restProps}
        variant="determinate"
        sx={(theme) => ({
          color:
            spinColor ??
            (isDtc
              ? brandclub_colors.lightGray
              : theme.vars.palette.text.primary),
          opacity: 0.15,
        })}
        value={100}
        size={size}
        thickness={thickness} // need more calculation for large size
      />
      <CircularProgress
        {...restProps}
        variant="indeterminate"
        disableShrink
        sx={(theme) => ({
          color:
            spinColor ??
            (isDtc
              ? brandclub_colors.lightGray
              : theme.vars.palette.text.primary),
          animationDuration: "1200ms",
          position: "absolute",
          left: 0,
          [`& .${circularProgressClasses.circle}`]: {
            strokeLinecap: "round",
            strokeDasharray: "30px,200px",
          },
        })}
        size={size}
        thickness={thickness} // need more calculation for large size
      />
    </div>
  );
};

const Loading = ({
  storeSpin,
  thickness = 7,
  dot,
  size,
  star,
  fullscreen = false,
  ellipsis,
  ellipsisBlue,
  ellipsisCustomColor,
  spinCustomColor,
}: {
  ellipsis?: boolean;
  ellipsisBlue?: boolean;
  ellipsisCustomColor?: string;
  thickness?: number;
  storeSpin?: boolean;
  dot?: boolean;
  size?: number;
  star?: boolean;
  fullscreen?: boolean;
  spinCustomColor?: string;
}) => {
  const theme = useTheme();
  const LoadingSymbol = (
    <div
      className="loading_animation"
      style={{
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
      }}
    >
      {ellipsisBlue || ellipsisCustomColor ? (
        <Lottie
          options={progressLottieOptions(
            theme,
            EllipsisBlueLoading,
            ellipsisCustomColor
          )}
          isClickToPauseDisabled
          width={size ?? 30}
          height={size ?? 30}
        />
      ) : ellipsis ? (
        <Lottie
          options={ellipsisOptions}
          isClickToPauseDisabled
          width={size ?? 30}
          height={size ?? 30}
        />
      ) : storeSpin ? (
        <StoreSpin
          size={size ?? 100}
          thickness={thickness ?? 7}
          spinColor={spinCustomColor}
        />
      ) : dot ? (
        <Lottie
          options={progressLottieOptions(
            theme,
            DotLoading,
            ellipsisCustomColor
          )}
          isClickToPauseDisabled
          width={size ?? 200}
          height="unset"
        />
      ) : star ? (
        <Lottie options={defaultOptions} isClickToPauseDisabled width={200} />
      ) : (
        <CircularProgress size={100} />
      )}
    </div>
  );

  if (fullscreen) {
    return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {LoadingSymbol}
      </div>
    );
  }

  return LoadingSymbol;
};

export default Loading;
